import { DataTable } from "@/components/data-table";
import { Form, useForm } from "@/components/form";
import { useGetDocumentosElectronicosQuery } from "@/features/facturacion-electronica/api/get-documentos-electronicos";
import { formatString, formatDate, formatNumber } from "@/shared/formats";
import { DEEstadoEnum, DETipoDocumentoEnum, type DocumentoElectronico } from "@/shared/types/documento-electronico";
import { Stack, StackProps, Chip, IconButton } from "@mui/material";
import { useFilters } from "@/components/filters";
import { Dialog, ModalHeader, useOpenModal } from "@/components/modal";
import AddIcon from '@mui/icons-material/Add';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useState } from "react";
import DEView from "./de-view";

interface FacturaManualSearchProps extends StackProps {
    onFacturaSelect?: (factura: DocumentoElectronico) => void;
}

export default function FacturaManualSearch({
    onFacturaSelect,
    ...props
}: FacturaManualSearchProps) {
    const openModal = useOpenModal();
    const [selectedDE, setSelectedDE] = useState<DocumentoElectronico | null>(null);

    const handleViewDetails = (de: DocumentoElectronico) => {
        setSelectedDE(de);
        openModal("viewDEDetails");
    };

    const filterData = useFilters({
        useURL: false,
        filters: [
            { type: "string", label: "CDC", name: "cdc" },
            { type: "string", label: "Receptor", name: "nombreReceptor" },
            { type: "dateRange", label: "Fecha Emisión", fromName: "fechaEmisionDesde", toName: "fechaEmisionHasta" },
            { type: "pagination" }
        ]
    });

    const queryResult = useGetDocumentosElectronicosQuery({
        ...filterData.filterData,
        tipoDocumento: DETipoDocumentoEnum.FacturaElectronica
    });

    const filterForm = useForm({
        defaultValues: filterData.filterData,
        onSubmit: ({ value }) => filterData.setFilterData(value),
        closeModalOnSubmit: false,
        submitOnce: false,
    });

    return <Stack minHeight={"70vh"} gap={3} {...props}>
        <Form form={filterForm} direction={"row"} flexWrap={"wrap"} gap={2}>
            <Form.TextField name="cdc" label="CDC" size="small" />
            <Form.TextField name="nombreReceptor" label="Receptor" size="small" />
            <Form.DatePicker name="fechaEmisionDesde" label="Desde" size="small" />
            <Form.DatePicker name="fechaEmisionHasta" label="Hasta" size="small" />
            <Form.SubmitButton>Filtrar</Form.SubmitButton>
        </Form>
        <DataTable flex={1}>
            <DataTable.Content
                fit="flex"
                rows={queryResult.data?.result?.filter(r => r.estado === DEEstadoEnum.Activo)}
                loading={queryResult.isFetching}
                columns={[
                    {
                        key: "numeroDocumento",
                        header: "Número",
                        render: (row: DocumentoElectronico) => `${row.numeroDocumento}`,
                        width: "150px"
                    },
                    {
                        key: "cdc",
                        header: "CDC",
                        render: (row: DocumentoElectronico) => (
                            <span style={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                                {row.cdc || 'Sin CDC'}
                            </span>
                        ),
                        width: "300px"
                    },
                    {
                        key: "nombreReceptor",
                        header: "Receptor",
                        render: (row: DocumentoElectronico) => formatString(row.nombreReceptor || 'Sin receptor')
                    },
                    {
                        key: "total",
                        header: "Total",
                        render: (row: DocumentoElectronico) => `${formatNumber(row.total, row.cMoneOpe == "PYG" ? 0 : 2)}`,
                        width: "120px"
                    },
                    {
                        key: "estado",
                        header: "Estado",
                        render: (row: DocumentoElectronico) => (
                            <Chip
                                label={row.estado === DEEstadoEnum.Activo ? 'Activo' : 'Deshabilitado'}
                                size="small"
                                color={row.estado === DEEstadoEnum.Activo ? 'success' : 'warning'}
                            />
                        ),
                        width: "100px"
                    },
                    {
                        key: "fechaEmisionDE",
                        header: "Fecha",
                        render: (row: DocumentoElectronico) => formatDate(row.fechaEmisionDE || row.fechaCreacion),
                        width: "100px"
                    },
                    {
                        header: "",
                        align: "center",
                        width: "80px",
                        render: (row: DocumentoElectronico) => (
                            <Stack direction="row" gap={0.5}>
                                <IconButton
                                    onClick={() => handleViewDetails(row)}
                                    size="medium"
                                    title="Ver detalles"
                                >
                                    <VisibilityIcon fontSize="small" />
                                </IconButton>
                                <IconButton
                                    onClick={() => onFacturaSelect?.(row)}
                                    size="medium"
                                    title="Seleccionar"
                                >
                                    <AddIcon fontSize="small" />
                                </IconButton>
                            </Stack>
                        )
                    }
                ]}
            />
            <DataTable.Pagination queryResult={queryResult} filterData={filterData} />
        </DataTable>

        <Dialog code="viewDEDetails" size="lg">
            <ModalHeader>Detalle del Documento Electrónico</ModalHeader>
            {selectedDE && <DEView de={selectedDE} simpleView />}
        </Dialog>
    </Stack>;
}
